import {
  presentB2CPaidModalAtom,
  presentSubscriptionCodeModalAtom,
  selectedCodeConfirmationAtom,
  presentNotificationModalAtom,
  showIntroAfterSubscriptionCodeModalAtom,
} from 'app/atoms/ui';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import { Event, track } from 'app/lib/analytics';
import { codeConfirmationText } from 'app/lib/code-confirmation';
import { useUser } from 'app/network/user';
import { isFreeTrialPlanCode, useMyActivePlans, useCreateOnePlan } from 'app/network/plan';
import { useOnePreference } from 'app/network/preferences';
import { useMyProfile } from 'app/network/profile';
import { createTheme as createDSTheme } from 'app/shopify';
import { TrialModalType } from 'app/types/preferences';
import { IconRosetteDiscountCheck } from '@tabler/icons-react-native';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const theme = createDSTheme();

/**
 * Subscription-code confirmation modal configured entirely by the API.
 * Configured prompts supply all their wording from the database.
 *
 * This modal is triggered from `useAppStartModals` (which runs in `landing-page`).
 * It must be rendered on `landing-page` so that it is mounted and visible when
 * `presentSubscriptionCodeModalAtom` is set to `true` by the modal orchestration logic.
 * Rendering it here (rather than on `all-listings`) ensures that
 * `presentSubscriptionCodeModalAtom` is properly cleared after the user responds,
 * which unblocks the `intro-after-signup` modal from firing.
 */
export function SubscriptionCodeModalPrompt() {
  const { i18n } = useTranslation();
  const [selectedConfirmation, setSelectedConfirmation] = useAtom(selectedCodeConfirmationAtom);
  const { data: user } = useUser();
  const currentPlan = useMyActivePlans().data?.[0];
  const prompt = selectedConfirmation?.prompt;

  const notificationsModalVisible = useAtomValue(presentNotificationModalAtom);
  const [isSubscriptionCodeModalOpen, setIsSubscriptionCodeModalOpen] = useAtom(presentSubscriptionCodeModalAtom);
  const [isActivating, setIsActivating] = useState(false);
  const setShowIntroAfterSubscriptionCodeModal = useSetAtom(showIntroAfterSubscriptionCodeModalAtom);
  const userTrialModalPreference = useOnePreference('trialModal').data?.value;
  const presentB2cPaidModal = useSetAtom(presentB2CPaidModalAtom);
  const { data: userProfile } = useMyProfile();
  const promptText = prompt ? codeConfirmationText(prompt, i18n.language, userProfile?.firstName) : undefined;

  useEffect(() => {
    if (selectedConfirmation && currentPlan && !isFreeTrialPlanCode(currentPlan.code.code)) {
      setIsSubscriptionCodeModalOpen(false);
      setSelectedConfirmation(null);
      setShowIntroAfterSubscriptionCodeModal(true);
    }
  }, [
    selectedConfirmation,
    currentPlan,
    setIsSubscriptionCodeModalOpen,
    setSelectedConfirmation,
    setShowIntroAfterSubscriptionCodeModal,
  ]);

  const { mutate: createPlan } = useCreateOnePlan({
    onSuccess: (_data, variables) => {
      setIsSubscriptionCodeModalOpen(false);
      setIsActivating(false);
      track(Event.SubscriptionCodePopupAccepted, { code: variables.code });
      setSelectedConfirmation(null);
      setShowIntroAfterSubscriptionCodeModal(true);

      if (userTrialModalPreference === TrialModalType.never) {
        presentB2cPaidModal(true);
      }
    },
    onError: () => {
      setIsActivating(false);
    },
  });

  const onActivationConfirmed = () => {
    if (
      isActivating ||
      !prompt ||
      selectedConfirmation?.userId !== user?.id ||
      !isFreeTrialPlanCode(currentPlan?.code.code)
    )
      return;
    setIsActivating(true);
    createPlan({ code: prompt.code, confirmationAccepted: true });
  };

  const onActivationDismissed = () => {
    if (isActivating) return;
    setSelectedConfirmation(null);
    setIsSubscriptionCodeModalOpen(false);
    setShowIntroAfterSubscriptionCodeModal(true);
  };

  const shouldShowModal =
    isSubscriptionCodeModalOpen &&
    !notificationsModalVisible &&
    Boolean(
      selectedConfirmation && selectedConfirmation.userId === user?.id && isFreeTrialPlanCode(currentPlan?.code.code),
    );

  return (
    <Modal onClose={onActivationDismissed} open={shouldShowModal} transparent>
      <Modal.Card gap="spacing.general.x6">
        <Column
          alignItems="center"
          gap="spacing.general.x4"
          paddingHorizontal="spacing.general.x4"
          paddingTop="spacing.general.x4"
        >
          <Column
            alignItems="center"
            backgroundColor="surface.success.subtle"
            borderRadius="radius.full"
            height={64}
            justifyContent="center"
            width={64}
          >
            <IconRosetteDiscountCheck color={theme.colors['surface.success.default']} size={32} strokeWidth={1.5} />
          </Column>

          <Column alignItems="center" gap="spacing.general.x2">
            {promptText?.eyebrow && (
              <Text
                color="surface.success.default"
                fontSize={12}
                fontWeight="700"
                letterSpacing={0.6}
                textAlign="center"
                textTransform="uppercase"
              >
                {promptText.eyebrow}
              </Text>
            )}
            <Text fontSize={22} fontWeight="700" lineHeight={27} textAlign="center">
              {promptText?.title}
            </Text>
            {promptText?.description && (
              <Text color="text.secondary" lineHeight={21} textAlign="center" variant="bodyBase">
                {promptText.description}
              </Text>
            )}
          </Column>
        </Column>

        <Column gap="spacing.general.x3" width="100%">
          <Button loading={isActivating} minHeight={48} onPress={onActivationConfirmed} variant="primary">
            {promptText?.yesLabel}
          </Button>

          <Button disabled={isActivating} minHeight={48} onPress={onActivationDismissed} variant="secondary">
            {promptText?.noLabel}
          </Button>
        </Column>
      </Modal.Card>
    </Modal>
  );
}
